import React, { useState } from 'react';
import { toast } from 'react-toastify';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useAddComment, useGetComments } from '../../services/hooks/loanCommentHooks';
import type { LoanApplicationComment } from '../../services/schemas/loanServiceSchemas/loanApplicationComment';
import type { LoanApplicationStatus } from '../../services/schemas/loanServiceSchemas/loanApplicationStatus';
import { formatDate } from '../../utils/formatters';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

interface LoanApplicationCommentsProps {
  loanApplicationId: string;
  currentStage: LoanApplicationStatus;
  className?: string;
}

const STAGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'LEAD_PROCESSING', label: 'Lead Processing' },
  { value: 'PENDING_SUBMISSION', label: 'Pending Submission' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'DOCUMENT_COLLECTION', label: 'Document Collection' },
  { value: 'VERIFICATION_IN_PROGRESS', label: 'Verification In Progress' },
  { value: 'VERIFICATION_REJECTED', label: 'Verification Rejected' },
  { value: 'VERIFICATION_COMPLETE', label: 'Verification Complete' },
  { value: 'EVALUATION_IN_PROGRESS', label: 'Evaluation In Progress' },
  { value: 'REJECTED_EVALUATION', label: 'Rejected Evaluation' },
  { value: 'AGREEMENT_PREPARATION', label: 'Agreement Preparation' },
  { value: 'AGREEMENT_READY', label: 'Agreement Ready' },
  { value: 'COLLATERAL_VERIFICATION', label: 'Collateral Verification' },
  { value: 'REJECTED_COLLATERAL', label: 'Rejected Collateral' },
  { value: 'PENDING_DISBURSEMENT', label: 'Pending Disbursement' },
  { value: 'DISBURSEMENT_IN_PROGRESS', label: 'Disbursement In Progress' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const getStageBadgeColor = (stage: string): string => {
  const stageColors: Record<string, string> = {
    DRAFT: 'secondary',
    LEAD_PROCESSING: 'info',
    PENDING_SUBMISSION: 'warning',
    SUBMITTED: 'primary',
    DOCUMENT_COLLECTION: 'info',
    VERIFICATION_IN_PROGRESS: 'warning',
    VERIFICATION_REJECTED: 'danger',
    VERIFICATION_COMPLETE: 'success',
    EVALUATION_IN_PROGRESS: 'warning',
    REJECTED_EVALUATION: 'danger',
    AGREEMENT_PREPARATION: 'info',
    AGREEMENT_READY: 'success',
    COLLATERAL_VERIFICATION: 'warning',
    REJECTED_COLLATERAL: 'danger',
    PENDING_DISBURSEMENT: 'info',
    DISBURSEMENT_IN_PROGRESS: 'primary',
    ACTIVE: 'success',
    COMPLETED: 'success',
    REJECTED: 'danger',
    CANCELLED: 'secondary',
  };
  return stageColors[stage] || 'secondary';
};

const columns: TableColumn<LoanApplicationComment>[] = [
  {
    name: 'Date',
    selector: (row) => row.createdDate || '',
    cell: (row) => (
      <span className="text-muted small">{formatDate(row.createdDate || '')}</span>
    ),
    sortable: true,
    width: '130px',
  },
  {
    name: 'Created By',
    selector: (row) => row.createdBy || '',
    cell: (row) => (
      <div className="d-flex align-items-center">
        <div
          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2 flex-shrink-0"
          style={{ width: '28px', height: '28px', fontSize: '12px' }}
        >
          <i className="fa fa-user"></i>
        </div>
        <span className="fw-semibold">{row.createdBy}</span>
      </div>
    ),
    sortable: true,
    width: '180px',
  },
  {
    name: 'Stage',
    selector: (row) => row.stage || '',
    cell: (row) => (
      <span className={`badge bg-${getStageBadgeColor(row.stage || '')} text-white`}>
        {row.stage?.replace(/_/g, ' ')}
      </span>
    ),
    sortable: true,
    width: '180px',
  },
  {
    name: 'Comment',
    selector: (row) => row.commentText || '',
    cell: (row) => (
      <div className="py-2" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {row.commentText}
      </div>
    ),
    grow: 2,
    wrap: true,
  },
];

const LoanApplicationComments: React.FC<LoanApplicationCommentsProps> = ({
  loanApplicationId,
  currentStage,
  className = '',
}) => {
  const [newComment, setNewComment] = useState('');
  const [filterStage, setFilterStage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Get user display name from Redux store
  const user = useSelector((state: RootState) => state.auth.user);

  // Fetch comments (server-side pagination)
  const {
    data: commentsResponse,
    isLoading,
    refetch: refetchComments,
  } = useGetComments(
    loanApplicationId,
    { stage: filterStage || undefined, page, size: pageSize },
    { enabled: !!loanApplicationId },
  );

  const comments: LoanApplicationComment[] = commentsResponse?.content ?? [];
  const totalRows = commentsResponse?.totalElements ?? 0;

  // Add comment mutation
  const addCommentMutation = useAddComment();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    setIsSubmitting(true);
    try {
      const createdByName = user?.name || user?.username || undefined;
      await addCommentMutation.mutateAsync({
        loanApplicationId,
        comment: newComment.trim(),
        createdBy: createdByName,
      });
      setNewComment('');
      toast.success('Comment added successfully');
      setPage(0);
      refetchComments();
    } catch (error: unknown) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage - 1); // DataTable is 1-indexed, API is 0-indexed
  };

  const handlePerRowsChange = (newPerPage: number) => {
    setPageSize(newPerPage);
    setPage(0);
  };

  return (
    <div className={`loan-application-comments ${className}`}>
      {/* Add Comment Form */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <h6 className="fw-bold mb-3">
            <i className="fa fa-plus-circle me-2 text-primary"></i>
            Add Comment
            <span className="text-muted fw-normal ms-2 small">(Current Stage: {currentStage?.replace(/_/g, ' ')})</span>
          </h6>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <textarea
                className="form-control"
                rows={3}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Enter your comment here..."
                disabled={isSubmitting}
                maxLength={2000}
              />
              <small className="text-muted">{newComment.length}/2000 characters</small>
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={isSubmitting || !newComment.trim()}
            >
              {isSubmitting ? (
                <React.Fragment>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Adding...
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <i className="fa fa-plus me-2"></i>
                  Add Comment
                </React.Fragment>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Comments Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-0 py-3 d-flex align-items-center justify-content-between">
          <h6 className="mb-0 fw-bold">
            <i className="fa fa-comments me-2 text-primary"></i>
            Comments &amp; Notes
          </h6>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0 small me-1">Filter by stage:</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={filterStage}
              onChange={(e) => {
                setFilterStage(e.target.value);
                setPage(0);
              }}
            >
              <option value="">All Stages</option>
              {STAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="card-body p-0">
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <DataTable
              columns={columns}
              data={comments}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={page + 1}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 20, 50]}
              paginationComponentOptions={{
                rowsPerPageText: 'Rows per page:',
                rangeSeparatorText: 'of',
              }}
              highlightOnHover
              striped
              responsive
              progressPending={isLoading}
              progressComponent={
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading comments...</span>
                  </div>
                  <p className="mt-2 text-muted mb-0">Loading comments...</p>
                </div>
              }
              noDataComponent={
                <div className="text-center py-4">
                  <i className="fa fa-comments fa-2x text-muted mb-2 d-block"></i>
                  <p className="text-muted mb-0">
                    {filterStage
                      ? `No comments found for stage: ${filterStage.replace(/_/g, ' ')}`
                      : 'No comments yet. Be the first to add a comment!'}
                  </p>
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanApplicationComments;